import React, { memo } from "react";
/**
 * @fileoverview ACTIVE CARD VIEW COMPONENT
 * @active Used in current Next.js App Router routes
 * 
 * THIS IS THE CURRENT ACTIVE CARD VIEW IMPLEMENTATION FOR COMPANIES.
 * It is rendered by NewCompanies.tsx when isCards=true in the Redux store.
 * 
 * Key features:
 * - Grid layout of company cards
 * - Filtering options
 * - Sorting capabilities
 * - Pagination with infinite scroll
 * 
 * CURRENT APP ROUTER COMPONENT HIERARCHY:
 * - page.tsx: Server component that fetches data
 *   └─ CompaniesWrapper.client.tsx: Client-side wrapper with "use client" directive
 *      └─ NewCompanies.tsx: Implements the view switching and data display
 *         └─ NewCompaniesCards (THIS FILE): CARD VIEW implementation (when isCards=true)
 *            └─ CompanyNewCardItem: Individual company card component
 * 
 * @see {@link src/components/companies/NewCompanies.tsx} - ACTIVE parent component
 * @see {@link src/components/companies/newCompaniesCards/SwitchViewBtns.tsx} - View switching controls
 * @see {@link src/components/companies/newCompaniesCards/companyNewCardItem/index.tsx} - Card item component
 * @see {@link src/store/newCompaniesList/newCompaniesList.selectors.ts} - Redux selectors for card data
 * @see {@link src/components/companies/companiesList/newCompaniesList/index.tsx} - Alternative TABLE/LIST VIEW

 * This component is part of the view switching system where:
 * 1. NewCompanies.tsx is the parent component that handles view switching
 * 2. This component (CARD VIEW) is rendered when isCards=true
 * 3. NewCompaniesList (TABLE/LIST VIEW) is rendered when isCards=false
 * 4. SwitchViewBtns toggles between these views
 * 
 * @see {@link src/components/companies/NewCompanies.tsx} - Parent component that handles view switching
 * @see {@link src/components/companies/companiesList/newCompaniesList/index.tsx} - TABLE VIEW implementation
 * @see {@link src/components/companies/newCompaniesCards/SwitchViewBtns.tsx} - Component for toggling between views
 * @see {@link src/components/companies/newCompaniesCards/companyNewCardItem/index.tsx} - Individual company card component
 * @see {@link src/components/companies/CompaniesWrapper.client.tsx} - Client wrapper used in App Router
 * @see {@link src/app/(main)/(home)/(research)/companies/page.tsx} - Current App Router page component
 * 
 * @note This component replaces the deprecated implementation in src/components/ui/cardCompanies/CardCompanies.jsx
 * @see {@link src/components/ui/cardCompanies/CardCompanies.jsx} - DEPRECATED implementation
 * @see {@link src/components/companies/companiesCard/CompaniesCard.jsx} - DEPRECATED container
 * @see {@link src/components/companies/Companies.tsx} - DEPRECATED page component
 */

import { Flex, Grid, Icon, Text, useDisclosure } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import FilterIcon from "@/assets/svg/filter.svg";
import { BreadcrumbsBlock } from "@/components/ui/BreadcrumbsBlock";
import { DrawerDefault } from "@/components/ui/drawer/DrawerDefault";
import { NoResultsComponent } from "@/components/ui/NoResultsComponent";
import { Spinner } from "@/components/ui/Spinner";
import { RoslindaleText } from "@/components/ui/text/RoslindaleText";
import { useCompaniesListMutation } from "@/services/newCompaniesList.service";
import filtersSelectors from "@/store/filters/filters.selectors";
import newCompaniesListSelectors from "@/store/newCompaniesList/newCompaniesList.selectors";
import newListSelectors from "@/store/newLists/newLists.slectors";

// Changed import from CompanyItem to Company as CompanyItem is not exported
import type { Company } from "../companiesList/newCompaniesList/types";

import CompanyCardFilter from "./companyCardFilter";
import CompanyNewCardItem from "./companyNewCardItem";
import SortCompaniesCards from "./SortCompaniesCards";
import SwitchViewBtns from "./SwitchViewBtns";

/**
 * Using CompanyItem from the types file for consistency
 * @see {@link src/components/companies/companiesList/newCompaniesList/types.ts}
 */

/**
 * Filter object structure
 */
interface Filter {
  key: string;
  value: string | number | boolean;
  operator?: string;
}

/**
 * Props for the NewCompaniesCards component
 */
interface NewCompaniesCardsProps {
  filters: Filter[];
  companies: Company[]; // Updated type from CompanyItem to Company
}

/**
 * Active card view component for displaying companies in a grid layout
 *
 * This is the current active implementation for the companies card view.
 * It is rendered by NewCompanies.tsx when isCards=true in the Redux store.
 *
 * @param props - Component props
 * @returns Rendered component
 */
const NewCompaniesCards: React.FC<NewCompaniesCardsProps> = ({
  filters,
  companies,
}) => {
  const isLoading = useSelector(
    newCompaniesListSelectors.getIsLoadingCompanies()
  );
  const [companiesList, { isLoading: pageIsLoading }] =
    useCompaniesListMutation({});

  const { isOpen, onOpen, onClose } = useDisclosure();

  const listParams = useSelector(newListSelectors.getListParams());
  const reqParams = useSelector(filtersSelectors.getParamsForCompaniesCards());

  const onLoadMoreHandler = (): void => {
    companiesList({
      params: {
        ...reqParams,
        limit: 24,
        page: listParams.page + 1,
      },
      isCards: true,
    });
  };

  return (
    <Flex
      direction={"column"}
      w={"100%"}
      maxW={"1440px"}
      mx={"auto"}
      px={{ base: "20px", md: "40px", lg: "45px" }}
      pt={"26px"}
    >
      <BreadcrumbsBlock
        {...{
          arrayPath: ["Home", "Research", "Companies"],
          objectName: "", // Adding required prop with empty string
        }}
      />
      <Flex align={"center"} justify={"space-between"} mt={"26px"}>
        <RoslindaleText>Companies</RoslindaleText>
        <SwitchViewBtns />
      </Flex>
      <Flex w={"100%"} columnGap={"20px"} py={"46.75px"}>
        <Flex
          minW={"248px"}
          w={"248px"}
          h={"fit-content"}
          pos={"relative"}
          display={{ base: "none", lg: "flex" }}
        >
          <CompanyCardFilter filters={filters} />
        </Flex>

        <DrawerDefault
          isOpen={isOpen}
          onClose={onClose}
          placement={"left"}
          size={"md"}
        >
          <CompanyCardFilter filters={filters} />
        </DrawerDefault>

        {!isLoading ? (
          <>
            {companies?.length ? (
              <Flex direction={"column"} rowGap={"24px"}>
                <Grid
                  w={"100%"}
                  h={"fit-content"}
                  templateColumns={{
                    base: "minmax(0, 1fr)",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(3, 1fr)",
                  }}
                  columnGap={"20px"}
                  rowGap={"20px"}
                  pos={"relative"}
                >
                  <Flex
                    pos={"absolute"}
                    left={"0"}
                    top={"-45px"}
                    align={"center"}
                    columnGap={"5px"}
                    cursor={"pointer"}
                    display={{ base: "flex", lg: "none" }}
                    onClick={onOpen}
                  >
                    <Text
                      fontSize={"14px"}
                      fontWeight={"400"}
                      lineHeight={"1.35"}
                      letterSpacing={"2%"}
                      mb={"-4px"}
                    >
                      Filter
                    </Text>
                    <Icon as={FilterIcon} w={"24px"} h={"24px"} />
                  </Flex>
                  <Flex pos={"absolute"} right={"0"} top={"-45px"}>
                    <SortCompaniesCards />
                  </Flex>
                  {companies?.map((company) => (
                    <CompanyNewCardItem key={company?.id} company={company} />
                  ))}
                </Grid>
                <Flex align={"center"} justify={"center"} w={"100%"}>
                  {!pageIsLoading ? (
                    <Text
                      cursor={"pointer"}
                      fontSize={"14px"}
                      fontWeight={"700"}
                      lineHeight={"1"}
                      letterSpacing={"0"}
                      color={"grey"}
                      textAlign={"center"}
                      onClick={onLoadMoreHandler}
                    >
                      Load more
                    </Text>
                  ) : (
                    <Spinner
                      customStyle={{ h: "24px" }}
                      bgBall1="#000000"
                      bgBall2="#000000"
                      delayBall1={{ interval: "2s", delay: "1s" }}
                      delayBall2={{ interval: "2s", delay: "1s" }}
                    />
                  )}
                </Flex>
              </Flex>
            ) : (
              <Grid placeItems={"center"} w={"100%"} h={"500px"}>
                <NoResultsComponent isAllCompanies={true} />
              </Grid>
            )}
          </>
        ) : (
          <Grid placeItems={"center"} w={"100%"} h={"500px"}>
            <Spinner
              bgBall1="#000000"
              bgBall2="#000000"
              delayBall1={{ interval: "2s", delay: "1s" }}
              delayBall2={{ interval: "2s", delay: "1s" }}
            />
          </Grid>
        )}
      </Flex>
    </Flex>
  );
};

export default memo(NewCompaniesCards);
